/**
 * VersichererBenachrichtigungen - the «Benachrichtigungen» tab on the Versicherer detail page (F1,
 * Foundation Slice B). THIN: it lists this firm's live notification addresses per (Prozess x
 * Kategorie), shows the Hauptadresse fallback hint + the read-only Brokercode VU, and renders the
 * «Hinzufügen» / Bearbeiten / Löschen controls only for carrier.manage. The full CRUD dialogs are a
 * later slice; the write endpoints already exist (POST/PATCH/DELETE .../notification-addresses).
 *
 * [Source: apps/web/src/features/entity-detail/VersichererDetailScreen.tsx] (AusschreibungCard idiom)
 */
import { Bell, AlertTriangle } from 'lucide-react';
import type {
  CarrierNotificationProzess,
  CarrierNotificationKategorie,
  InsurerNotificationAddressesDto,
} from '@brokly/shared';
import { Button } from '../../../components/ui/Button';

const PROZESS_LABELS: Record<CarrierNotificationProzess, string> = {
  p4_analyse: 'P4 Analyse',
  p5_broker: 'P5 Broker',
  p6_aufloesung: 'P6 Mandatsauflösung',
};
const KATEGORIE_LABELS: Record<CarrierNotificationKategorie, string> = {
  privat: 'Privat',
  firmen: 'Firmen',
  alle: 'Alle',
};

export function VersichererBenachrichtigungen({
  data,
  canManage,
  onAdd,
  onEdit,
  onDelete,
  onToggleManualReportOnly,
}: {
  data: InsurerNotificationAddressesDto | undefined;
  canManage: boolean;
  onAdd?: () => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  /** The firm's flag «nur manuelle Meldung» changed (carrier.manage). */
  onToggleManualReportOnly?: (value: boolean) => void;
}) {
  if (!data) {
    return <p className="rounded-lg border bg-card p-8 text-center text-sm text-muted-foreground">Lade Benachrichtigungsadressen …</p>;
  }

  return (
    <div className="flex flex-col gap-4" data-testid="versicherer-benachrichtigungen">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <Bell className="size-4 text-muted-foreground" aria-hidden /> Benachrichtigungen
          </h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Eigene Benachrichtigungsadressen dieser Firma je Prozess und Kategorie (Privat/Firmen)
          </p>
        </div>
        {canManage && (
          <Button size="sm" variant="outline" data-testid="benachr-add" onClick={() => onAdd?.()}>
            Hinzufügen
          </Button>
        )}
      </div>

      {data.items.length === 0 ? (
        <p className="rounded-lg border bg-card p-6 text-center text-sm text-muted-foreground">
          Noch keine Benachrichtigungsadresse erfasst. Es wird die Hauptadresse verwendet.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {data.items.map((item) => (
            <li
              key={item.id}
              data-testid="benachr-row"
              className="flex items-center justify-between gap-3 rounded-lg border bg-card px-3 py-2"
            >
              <div className="flex flex-col gap-0.5">
                <span className="text-xs text-muted-foreground">
                  {PROZESS_LABELS[item.prozess]} · {KATEGORIE_LABELS[item.kategorie]}
                </span>
                <span className="text-sm text-foreground">{item.email}</span>
                {item.domain_warning !== null && (
                  <span
                    data-testid="benachr-warning"
                    className="mt-0.5 flex items-center gap-1 text-xs text-status-warning"
                  >
                    <AlertTriangle className="size-3" aria-hidden /> Domain weicht ab
                  </span>
                )}
              </div>
              {canManage && (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" data-testid="benachr-edit" onClick={() => onEdit?.(item.id)}>
                    Bearbeiten
                  </Button>
                  <Button size="sm" variant="outline" data-testid="benachr-delete" onClick={() => onDelete?.(item.id)}>
                    Löschen
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-col gap-1 rounded-lg border bg-card p-3">
        <span className="text-xs text-muted-foreground">Hauptadresse (Fallback)</span>
        <span className="text-sm text-foreground" data-testid="benachr-fallback">
          {data.carrier_default_email ?? '-'}
        </span>
        <span className="text-xs text-muted-foreground">
          Fehlt eine Adresse, wird diese Hauptadresse verwendet und eine Aufgabe erstellt.
        </span>
      </div>

      <div className="flex flex-col gap-1 rounded-lg border bg-card p-3">
        {canManage ? (
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              aria-label="Nur manuelle Meldung"
              checked={data.manual_report_only}
              onChange={(e) => onToggleManualReportOnly?.(e.target.checked)}
            />
            Nur manuelle Meldung
          </label>
        ) : (
          <span className="text-sm text-foreground" data-testid="benachr-manual-only">
            {data.manual_report_only ? 'Nur manuelle Meldung' : 'Automatische Meldung'}
          </span>
        )}
        <span className="text-xs text-muted-foreground">
          Meldungen eines Brokermandats an diesen Versicherer gehen nur nach Freigabe durch den Broker hinaus.
        </span>
      </div>

      <div className="flex flex-col gap-1 rounded-lg border bg-card p-3">
        <span className="text-xs text-muted-foreground">Brokercode VU (nur Ansicht)</span>
        <span className="text-sm text-foreground" data-testid="benachr-vu">{data.broker_vu_code ?? '-'}</span>
        <span className="text-xs text-muted-foreground">
          Wird in den Courtage-Stammdaten gepflegt (Administration &rsaquo; Broker-Identität).
        </span>
      </div>
    </div>
  );
}
